import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Field, PageHeader } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const EMPRESA = [["razon_social", "Razón social"], ["rut", "RUT"], ["giro", "Giro"], ["direccion", "Dirección"], ["telefono", "Teléfono"], ["email", "Email"]];

const LogoUpload = ({ url, canEdit, onSaved }) => {
  const up = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try { await api.post("/admin/settings/logo", fd); toast.success("Logo actualizado"); onSaved(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="flex items-center gap-4" data-testid="logo-upload">
      <div className="h-16 w-40 rounded-xl border border-dashed border-[#9fb196] grid place-items-center bg-white overflow-hidden">
        {url ? <img data-testid="logo-preview" src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="Logo" className="max-h-14 max-w-[150px] object-contain" /> : <span className="text-xs text-[#5b6656]">Sin logo</span>}
      </div>
      {canEdit && <label className="cursor-pointer text-sm font-semibold text-[#258F00]"><input data-testid="logo-file-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={up} />Subir logo (PNG/JPG, máx. 2 MB)</label>}
    </div>
  );
};
const SEGURIDAD = [["max_attempts", "Intentos fallidos antes de bloqueo"], ["lock_minutes", "Minutos de bloqueo"], ["idle_minutes", "Timeout por inactividad (min)"], ["refresh_hours", "Duración máxima de sesión (horas)"], ["password_min", "Largo mínimo de contraseña"]];

const Section = ({ sectionKey, value, onSaved, canEdit, children }) => {
  const save = async () => {
    try { await api.put(`/admin/settings/${sectionKey}`, value); toast.success("Configuración guardada"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="syc-card p-6 space-y-5 max-w-3xl" data-testid={`settings-section-${sectionKey}`}>
      {children}
      {canEdit && <Button data-testid={`save-settings-${sectionKey}-btn`} className="rounded-full" onClick={save}>Guardar</Button>}
    </div>
  );
};

const FieldsGrid = ({ fields, value, onChange, disabled, type = "text", prefix }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    {fields.map(([k, l]) => (
      <Field key={k} label={l}>
        <Input data-testid={`${prefix}-${k}-input`} type={type} disabled={disabled} value={value[k] ?? ""}
          onChange={(e) => onChange({ ...value, [k]: type === "number" ? Number(e.target.value) : e.target.value })} />
      </Field>
    ))}
  </div>
);

const Correlativos = ({ value, onChange, disabled, preview }) => (
  <div className="space-y-3">
    <p className="text-sm text-[#5b6656]">Variables disponibles: <code className="font-mono">{"{AÑO}"}</code> y <code className="font-mono">{"{CORRELATIVO}"}</code> (obligatoria).</p>
    {Object.entries(value).map(([k, v]) => (
      <div key={k} className="grid grid-cols-[60px_1fr_90px] sm:grid-cols-[80px_1fr_110px_160px] gap-3 items-end">
        <div className="font-mono font-bold text-sm pb-2">{k}</div>
        <Field label="Formato"><Input data-testid={`correlativo-${k}-format`} disabled={disabled} value={v.format} onChange={(e) => onChange({ ...value, [k]: { ...v, format: e.target.value } })} /></Field>
        <Field label="Dígitos"><Input data-testid={`correlativo-${k}-padding`} type="number" disabled={disabled} value={v.padding} onChange={(e) => onChange({ ...value, [k]: { ...v, padding: Number(e.target.value) } })} /></Field>
        <div className="hidden sm:block pb-2 font-mono text-xs text-[#258F00]" data-testid={`correlativo-${k}-preview`}>{preview?.[k]}</div>
      </div>
    ))}
  </div>
);

export default function Settings() {
  const { can } = useAuth();
  const [s, setS] = useState(null);
  const canEdit = can("admin.configuracion.editar");
  const load = () => api.get("/admin/settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  if (!s) return <div className="text-[#5b6656]">Cargando…</div>;
  const set = (k) => (v) => setS({ ...s, [k]: v });

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Administración" title="Configuración" subtitle="Parámetros administrables sin modificar código. Cada cambio queda auditado." />
      <Tabs defaultValue="empresa">
        <TabsList className="mb-5 flex-wrap h-auto">
          {[["empresa", "Empresa"], ["seguridad", "Seguridad"], ["correlativos", "Correlativos"], ["documentos", "Documentos"], ["email", "Email"]].map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`settings-tab-${k}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="empresa"><Section sectionKey="empresa" value={s.empresa} onSaved={load} canEdit={canEdit}><LogoUpload url={s.empresa.logo_url} canEdit={canEdit} onSaved={load} /><FieldsGrid prefix="empresa" fields={EMPRESA} value={s.empresa} onChange={set("empresa")} disabled={!canEdit} /></Section></TabsContent>
        <TabsContent value="seguridad"><Section sectionKey="seguridad" value={s.seguridad} onSaved={load} canEdit={canEdit}><FieldsGrid prefix="seguridad" type="number" fields={SEGURIDAD} value={s.seguridad} onChange={set("seguridad")} disabled={!canEdit} /></Section></TabsContent>
        <TabsContent value="correlativos"><Section sectionKey="correlativos" value={s.correlativos} onSaved={load} canEdit={canEdit}><Correlativos value={s.correlativos} onChange={set("correlativos")} disabled={!canEdit} preview={s.correlativos_preview} /></Section></TabsContent>
        <TabsContent value="documentos">
          <Section sectionKey="documentos" value={s.documentos} onSaved={load} canEdit={canEdit}>
            <Field label="Encabezado de documentos"><Textarea data-testid="documentos-encabezado-input" disabled={!canEdit} value={s.documentos.encabezado} onChange={(e) => set("documentos")({ ...s.documentos, encabezado: e.target.value })} /></Field>
            <Field label="Pie de documentos"><Textarea data-testid="documentos-pie-input" disabled={!canEdit} value={s.documentos.pie} onChange={(e) => set("documentos")({ ...s.documentos, pie: e.target.value })} /></Field>
          </Section>
        </TabsContent>
        <TabsContent value="email">
          <Section sectionKey="email" value={s.email} onSaved={load} canEdit={canEdit}>
            <Field label="Remitente" hint="Dirección autorizada por S&C."><Input data-testid="email-remitente-input" disabled value={s.email.remitente} /></Field>
            <Field label="Nombre del remitente"><Input data-testid="email-nombre-input" disabled={!canEdit} value={s.email.nombre_remitente} onChange={(e) => set("email")({ ...s.email, nombre_remitente: e.target.value })} /></Field>
            <div className="rounded-lg border border-[#FFE100] bg-[#fffbe0] p-3 text-sm" data-testid="smtp-pending-note">El servidor SMTP de abastecimiento@sycspa.cl aún no está configurado. Mientras tanto, los correos se registran como simulados.</div>
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
